import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { getSensor, addSensor, editSensor } from '../../../services/sensors/sensorsService';
import { getSoilSensors} from '../../../services/soilsensors/soilSensorsService';
import { getSimModules} from '../../../services/simModules/simModulesServices';
import { VARIABLES} from '../../utils/Constants';

import AutocompleteSimcard from '../../../shared/components/autocompleteSimcard/AutocompleteSimcard';
import VariableCard from '../variableCard/VariableCard'

const BOOLEAN_OPTIONS = [
    { label: 'Si', value: true },
    { label: 'No', value: false },
];

const dataForVariable = (threshold, isSonda = false, soilNumber = null) => {
    return VARIABLES.filter(v => {
        // Si es una sonda, filtrar las variables que pertenecen a esa sonda
        if (isSonda) {
            return v.sonda; // Solo variables de sonda
        }
        // Si no es una sonda, filtrar las variables que no son de sonda
        return !v.sonda;
    }).map(v => {
        // Buscar el threshold correspondiente
        const resultado = threshold.find(thr => {
            // Si es una sonda, comparar el número de la sonda
            if (isSonda) {
                // Extraer el número de la sonda del thr.variableId (ej: "TS2" -> 2)
                const thrSoilNumber = parseInt(thr.variableId.replace(/\D/g, "")) || 1;
                // Comparar el thrSoilNumber con el soilNumber de la sonda actual
                return thrSoilNumber === soilNumber && thr.variableId.startsWith(v.variableId);
            }
            // Si no es una sonda, comparar directamente los variableId
            return thr.variableId === v.variableId;
        });

        return {
            variableId: v.variableId,
            label: v.label,
            unit: v.unit,
            isSelected: !!resultado, // true si existe resultado, false si no
            maxValue: (resultado && resultado.maxValue !== undefined) ? resultado.maxValue : null, // Valor predeterminado: 1
            minValue: (resultado && resultado.minValue !== undefined) ? resultado.minValue : null, // Valor predeterminado: 1
            maxLimit: (resultado && resultado.maxLimit !== undefined) ? resultado.maxLimit : v.maxLimit || 1, // Valor predeterminado: 1
            minLimit: (resultado && resultado.minLimit !== undefined) ? resultado.minLimit : v.minLimit || 1, // Valor predeterminado: 1
        };
    });
};

const buildThresholdForSend = (data) => {
    return [
        // Variables no asociadas a sondas
        ...data.variablesNoSonda.filter(v => v.isSelected).map(v => ({
            variableId: v.variableId,
            maxValue: Number(v.maxValue), 
            minValue: Number(v.minValue),
            maxLimit: Number(v.maxLimit),
            minLimit: Number(v.minLimit),
        })),
        
        // Variables asociadas a sondas
        ...data.sondas.flatMap( (sonda, sondaIndex) => 
            sonda.variables.filter(v => v.isSelected).map((v) => ({
                variableId: (sondaIndex + 1 < 2) ? v.variableId :  `${v.variableId}${sondaIndex + 1}`,                   
                maxValue: Number(v.maxValue), 
                minValue: Number(v.minValue),
                maxLimit: Number(v.maxLimit),
                minLimit: Number(v.minLimit),
            }))
        ),
    ];
};

const buildSensorForSend = (originalObject) => {
    return {
        id: originalObject.id,
        name: originalObject.name,
        active: originalObject.active,
        checked: originalObject.checked,
        startOperation: originalObject.startOperation,
        sendMail: originalObject.sendMail,
        description: originalObject.description,
        sim: originalObject.sim, // Mantener el objeto sim completo
        simModuleId: originalObject.simModuleId,
        gps: originalObject.gps,
        observation: originalObject.observation,
    }
};




const SensorAdView = () => {
    const { sensorId } = useParams();
    const navigate = useNavigate();
    const [soilTypes, setSoilTypes] = useState([]);
    const [simModules, setSimModules] = useState([]);

    const { register, handleSubmit, reset,  control,formState: {errors}, watch } = useForm({
            defaultValues:{
                active: '',
                sendMail: '',
                sim: '',
                simModuleId: '',
                gps: '',
                // Variables no asociadas a sondas
                variablesNoSonda: dataForVariable([], false),
                // Sondas con sus variables asociadas
                sondas: []// sondas: [{ soilIdSensor: '', variables: dataForVariable([], false)}],

            }
    });

    const { fields: sondaFields, append: appendSonda, remove: removeSonda } = useFieldArray({
        control,
        name: 'sondas',
    });
    

    useEffect(() => {
        loadSoilSensors();
        loadSimModules();
        const fetchSensor = async () => {
            if (sensorId !== 'newSensor') {
                try {
                    const response = await getSensor(sensorId);
                    const sortedSoilSensor = response.soilSensor.sort((a, b) => a.soilNumber - b.soilNumber);
                    reset({
                        ...response.sensor,
                        startOperation: response.sensor.startOperation.split('T')[0],
                        variablesNoSonda: dataForVariable(response.threshold, false),
                        sondas: sortedSoilSensor.map(soil => ({
                            soilIdSensor: soil.soilIdSensor,
                            variables: dataForVariable(response.threshold, true, soil.soilNumber),
                        })),
                    });
                } catch (error) {
                    console.error(error);
                }
            }
        };

        fetchSensor();
    }, [sensorId,reset]);

    const loadSoilSensors = async () => {
        try {
            const response = await getSoilSensors(1, 10);
            setSoilTypes(response);

        } catch (error) {
            console.error('Error al cargar los sensores del suelo: ', error);
            alert("Error al cargar los sensores del suelo.");
        }
    }

    const loadSimModules = async () => {
        try {
            const response = await getSimModules(1, 10);
            setSimModules(response);

        } catch (error) {
            console.error('Error al cargar los modulos sim', error);
            alert("Error al cargar los modulos sim.");
        }
    }

    const onSubmit = handleSubmit  (async (data) => {
        // event.preventDefault();
        // console.log(data);
        const sensor = buildSensorForSend(data);
        const threshold = buildThresholdForSend(data);
        const soilSensor = data.sondas.map((sonda, index) => ({
            soilNumber: index + 1, // Asignar un número basado en el índice (empezando desde 1)
            soilIdSensor: sonda.soilIdSensor, // Convertir el ID de la sonda a cadena si es necesario
        }));

        
        // console.log("sensor: ", sensor);
        // console.log("threshold: ", threshold);
        // console.log("soilData: ", soilData);
        const body = {
            sensor: sensor,
            threshold: threshold,
            soilSensor: soilSensor 
        };            

        try {
            if (sensorId !== 'newSensor') {
                const response = await editSensor(sensorId, body);
                if(response.id)
                    alert("Sensor actualizado correctamente");
            }
            else 
            {
                const response = await addSensor(body);
                if(response.id)
                    alert("Sensor creado correctamente");
            }
            navigate('/sensors-ad');
            
        } catch (error) {
            console.error('Error al enviar el formulario: ', error);
            alert("Error al enviar el formulario. Por favor, intenta de nuevo.");
        } 
    });

    const handleCancel = () => {
        navigate('/sensors-ad');
    };


    return(
        <div className='flex flex-col p-3'>
            <div className="flex max-sm:flex-col items-center justify-between mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">Sensor</h1>
            </div>

            <div className="w-full max-w-5xl mx-auto">
                <form onSubmit={onSubmit} className="flex flex-col items-center space-y-4 pb-2">
                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="id" className="block font-semibold text-black mb-1"> Id <span className="text-red-500">*</span></label>
                            <input id="id" type="text" autoComplete="off"
                                name="id"
                                placeholder="Id"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('id',{
                                    required: {
                                        value: true,
                                        message: 'Id es requerido'
                                    },
                                    minLength: {
                                        value: 6,
                                        message: "La longitud mínima debe ser de 6 caracteres."
                                    },
                                    maxLength: {
                                        value: 6,
                                        message: "La longitud máxima debe ser de 6 caracteres."
                                    }
                                })}
                            />
                            {
                                errors.id && <span className="error">{errors.id.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="name" className="block font-semibold text-black mb-1"> Nombre <span className="text-red-500">*</span></label>
                            <input id="name" type="text" autoComplete="off"
                                name="name"
                                placeholder="Nombre"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('name',{
                                    required: {
                                        value: true,
                                        message: 'Nombre es requerido'
                                    },
                                    minLength:{
                                        value: 2,
                                        message: 'Nombre debe tener al menos 2 caracteres'
                                    },
                                    maxLength:{
                                        value: 30,
                                        message: 'Nombre debe tener máximo 30 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.name && <span className="error">{errors.name.message}</span>
                            }                            
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="active" className="block font-semibold text-black mb-1"> Activo <span className="text-red-500">*</span></label>
                            <select id="active"
                                name="active" 
                                {...register("active",{
                                    required: {
                                        value: true,
                                        message: 'Activo es requerido'
                                    }    
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {BOOLEAN_OPTIONS.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.active && <span className="error">{errors.active.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="sendMail" className="block font-semibold text-black mb-1"> Correo de alerta <span className="text-red-500">*</span></label>
                            <select id="sendMail" 
                                name="sendMail" 
                                {...register("sendMail",{
                                    required: {
                                        value: true,
                                        message: 'Operador es requerido'
                                    }    
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {BOOLEAN_OPTIONS.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.sendMail && <span className="error">{errors.sendMail.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="startOperation" className="block font-semibold text-black mb-1"> Inicio de operaciones <span className="text-red-500">*</span></label>
                            <input id="startOperation" type="date" autoComplete="off"
                                name="startOperation"
                                placeholder="Inicio de operaciones"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('startOperation',{
                                    required: {
                                        value: true,
                                        message: 'Inicio de operaciones es requerido'
                                    }
                                })}
                            />
                            {
                                errors.startOperation && <span className="error">{errors.startOperation.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="sim" className="block font-semibold text-black mb-1"> Simcard <span className="text-red-500">*</span></label>  
                            <Controller
                                name="sim"
                                control={control}
                                rules={{ required: "Debe seleccionar una simcard" }}
                                render={({ field, fieldState: { error } }) => (
                                    <div>
                                        <AutocompleteSimcard
                                            {...field}
                                            id="sim"
                                            placeholder="Simcard"
                                        />
                                        {error && (
                                            <span className="error">
                                                {error.message}
                                            </span>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="simModuleId" className="block font-semibold text-black mb-1"> Modulo Sim <span className="text-red-500">*</span></label>
                            <select id="simModuleId"
                                name="simModuleId" 
                                {...register("simModuleId",{
                                    required: {
                                        value: true,
                                        message: 'Modulo Sim es requerido'
                                    }    
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {simModules.map((option, index) => (
                                    <option key={index} value={option.id}>{option.name}</option>
                                ))}
                            </select>
                            {
                                errors.simModuleId && <span className="error">{errors.simModuleId.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="gps" className="block font-semibold text-black mb-1"> Tiene GPS <span className="text-red-500">*</span></label>
                            <select id="gps" 
                                name="gps" 
                                {...register("gps",{
                                    required: {
                                        value: true,
                                        message: 'Operador es requerido'
                                    }    
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {BOOLEAN_OPTIONS.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.gps && <span className="error">{errors.gps.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="description" className="block font-semibold text-black mb-1"> Descripción <span className="text-red-500">*</span></label>
                            <textarea id="description" autoComplete="off"
                                name="description"
                                placeholder="Descripción"
                                rows="4" 
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer resize-none"
                                {...register('description',{
                                    required: {
                                        value: true,
                                        message: 'Descripción es requerido'
                                    },
                                    minLength:{
                                        value: 2,
                                        message: 'Descripción debe tener al menos 2 caracteres'
                                    },
                                    maxLength:{
                                        value: 255,
                                        message: 'Descripción debe tener máximo 255 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.description && <span className="error">{errors.description.message}</span>
                            }
                            
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="observation" className="block font-semibold text-black mb-1"> Observación </label>
                            <textarea id="observation" autoComplete="off"
                                name="observation"
                                placeholder="Observación"
                                rows="4" 
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer resize-none"
                                {...register('observation',{
                                    maxLength:{
                                        value: 255,
                                        message: 'Observación debe tener máximo 255 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.observation && <span className="error">{errors.observation.message}</span>
                            }
                            
                        </div>
                    </div>
                    
                    <div className="flex-col items-center justify-center w-full my-4">
                        <h2 className="text-2xl font-semibold text-blue-600 text-center mb-4">Configuración de umbrales</h2>
                    
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"> {/*grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6*/}
                            {watch('variablesNoSonda').map((variable, index) => (
                                <div key={variable.variableId || `fallback-key-${index}`} className="w-full">
                                    <VariableCard
                                        key={`${variable.variableId}.${index}`}
                                        variable={variable}
                                        register={register}
                                        index={index}
                                        prefix="variablesNoSonda"
                                        watch={watch}
                                        unit={variable.unit}
                                        errors={errors}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="flex-col items-center justify-center w-full my-4">
                        {/* Variables asociadas a sondas */}
                        <h2 className="text-2xl font-semibold text-blue-600 text-center mb-4">Configuración de sondas</h2>

                        {sondaFields.map((sonda, sondaIndex) => (
                            <div key={sonda.id} className="border rounded-lg p-4 bg-white shadow-sm mb-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-blue-600 italic">
                                        Sonda {sondaIndex + 1}
                                    </h3>

                                    <button type="button"
                                        onClick={() => removeSonda(sondaIndex)}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-gray-100 rounded-lg border border-red-200 hover:border-red-500 hover:bg-red-500 transition-colors duration-200"
                                    >
                                        <span className="material-icons-outlined mr-2">delete</span> Eliminar sonda
                                    </button>
                                </div>

                                <div className='w-full mb-4'>
                                    {/* Selector de tipo de sonda */}
                                    <label htmlFor={`soilIdSensor-${sondaIndex}`} className="block font-semibold text-black mb-1">
                                        Tipo de sonda <span className="text-red-500">*</span>
                                    </label>
                                    <Controller
                                        name={`sondas.${sondaIndex}.soilIdSensor`}
                                        control={control}
                                        rules={{ required: 'Tipo de sonda es requerido' }}
                                        render={({ field }) => (
                                            <select
                                            {...field}
                                            id={`soilIdSensor-${sondaIndex}`}
                                            className="block w-full md:w-1/2 p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                            >
                                            <option value="">Seleccione</option>
                                            {soilTypes.length > 0 &&
                                                soilTypes.map((option, index) => (
                                                <option key={index} value={option.id}>
                                                    {option.id}
                                                </option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                </div>

                                {/* Variables de la sonda */}
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {watch(`sondas.${sondaIndex}.variables`).map((variable, index) => (
                                        <div key={variable.variableId || `fallback-key-${index}`} className="w-full">
                                            <VariableCard
                                                key={`${variable.variableId}.${index}`}
                                                variable={variable}
                                                register={register}
                                                index={index}
                                                prefix={`sondas.${sondaIndex}.variables`}
                                                watch={watch}
                                                unit={variable.unit}
                                                errors={errors}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Botón para agregar una nueva sonda */}
                        <div className="w-full max-w-xl mx-auto">
                            <button
                                type="button"
                                onClick={() => appendSonda({ soilIdSensor: '', variables: dataForVariable([], true) })}
                            className="flex flex-1 rounded w-full justify-center text-white bg-blue-600 hover:bg-blue-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200"
                            >
                            Agregar sonda
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center md:space-x-4">
                        <div className="w-full">
                            <button type="submit" className="flex flex-1 rounded w-full justify-center text-white bg-blue-600 hover:bg-blue-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200">
                                Guardar
                            </button>
                        </div>

                        <div className="w-full">
                            <button type="button" onClick={handleCancel} className="flex flex-1 rounded w-full justify-center text-white bg-red-600 hover:bg-red-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200">
                                Cancelar
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default SensorAdView;